import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAnchor,
  faBath,
  faBellConcierge,
  faBicycle,
  faBinoculars,
  faBowlingBall,
  faBriefcase,
  faBriefcaseMedical,
  faBroom,
  faCar,
  faCircleCheck,
  faClock,
  faDice,
  faDumbbell,
  faElevator,
  faEnvelope,
  faFish,
  faGift,
  faGolfBallTee,
  faHotTub,
  faKitchenSet,
  faLanguage,
  faMasksTheater,
  faMoneyBillTransfer,
  faMountain,
  faMugHot,
  faPersonHiking,
  faPersonSkiing,
  faPersonSwimming,
  faPumpSoap,
  faSailboat,
  faScissors,
  faShieldHalved,
  faShirt,
  faShower,
  faSink,
  faSnowflake,
  faSoap,
  faSpa,
  faTableTennisPaddleBall,
  faTemperatureHigh,
  faToilet,
  faTv,
  faUmbrellaBeach,
  faUsers,
  faUtensils,
  faWater,
  faWifi,
  faWind,
} from '@fortawesome/free-solid-svg-icons';

// Category drives the icon color (see condo-amenities/style.scss). Amenities that
// don't match a category fall back to 'default' (the light blue version).
export type CondoAmenityCategory = 'bathroom' | 'climate' | 'services' | 'recreation' | 'default';

interface ICondoAmenityRule {
  match: string;
  icon: IconDefinition;
  category: CondoAmenityCategory;
}

// Resolved top-to-bottom: the first rule whose `match` is contained in the
// (lower-cased) amenity name wins, so more specific rules go first. This keyword
// approach mirrors how hotels resolve icons by name (see utils/amenities.tsx) but
// uses monochrome icons so the color can be set per category.
const CONDO_AMENITY_RULES: ICondoAmenityRule[] = [
  // Climate & tech
  { match: 'air condition', icon: faSnowflake, category: 'climate' },
  { match: 'alarm', icon: faClock, category: 'climate' },
  { match: 'clock radio', icon: faClock, category: 'climate' },
  { match: 'dvd', icon: faTv, category: 'climate' },
  { match: 'television', icon: faTv, category: 'climate' },
  { match: 'wifi', icon: faWifi, category: 'climate' },
  { match: 'wireless', icon: faWifi, category: 'climate' },
  { match: 'internet', icon: faWifi, category: 'climate' },
  { match: 'heated', icon: faTemperatureHigh, category: 'climate' },
  { match: 'heating', icon: faTemperatureHigh, category: 'climate' },

  // Bathroom
  { match: 'toiletr', icon: faPumpSoap, category: 'bathroom' },
  { match: 'bath countertop', icon: faSink, category: 'bathroom' },
  { match: 'countertop', icon: faSink, category: 'bathroom' },
  { match: 'bathtub', icon: faBath, category: 'bathroom' },
  { match: 'bath tub', icon: faBath, category: 'bathroom' },
  { match: 'steam bath', icon: faShower, category: 'bathroom' },
  { match: 'shower', icon: faShower, category: 'bathroom' },
  { match: 'hair dryer', icon: faWind, category: 'bathroom' },
  { match: 'toilet', icon: faToilet, category: 'bathroom' },
  { match: 'sink', icon: faSink, category: 'bathroom' },
  { match: 'bath', icon: faBath, category: 'bathroom' },

  // Services
  { match: 'business', icon: faBriefcase, category: 'services' },
  { match: 'conference', icon: faBriefcase, category: 'services' },
  { match: 'meeting', icon: faBriefcase, category: 'services' },
  { match: 'dry cleaning', icon: faShirt, category: 'services' },
  { match: 'laundry service', icon: faShirt, category: 'services' },
  { match: 'drying rack', icon: faShirt, category: 'services' },
  { match: 'washer', icon: faSoap, category: 'services' },
  { match: 'dryer', icon: faSoap, category: 'services' },
  { match: 'iron', icon: faShirt, category: 'services' },
  { match: 'laundry', icon: faSoap, category: 'services' },
  { match: 'vacuum', icon: faBroom, category: 'services' },
  { match: 'concierge', icon: faBellConcierge, category: 'services' },
  { match: 'front desk', icon: faBellConcierge, category: 'services' },
  { match: 'currency', icon: faMoneyBillTransfer, category: 'services' },
  { match: 'post office', icon: faEnvelope, category: 'services' },
  { match: 'medical', icon: faBriefcaseMedical, category: 'services' },
  { match: 'multilingual', icon: faLanguage, category: 'services' },
  { match: 'language', icon: faLanguage, category: 'services' },
  { match: 'staff', icon: faUsers, category: 'services' },
  { match: 'gift shop', icon: faGift, category: 'services' },
  { match: 'newsstand', icon: faGift, category: 'services' },
  { match: 'safetyequipment', icon: faShieldHalved, category: 'services' },
  { match: 'safety equipment', icon: faShieldHalved, category: 'services' },
  { match: 'salon', icon: faScissors, category: 'services' },
  { match: 'spa', icon: faSpa, category: 'services' },

  // Activities — land sports (green)
  { match: 'bicycl', icon: faBicycle, category: 'recreation' },
  { match: 'biking', icon: faBicycle, category: 'recreation' },
  { match: 'cycling', icon: faBicycle, category: 'recreation' },
  { match: 'golf', icon: faGolfBallTee, category: 'recreation' },
  { match: 'hiking', icon: faPersonHiking, category: 'recreation' },
  { match: 'hike', icon: faPersonHiking, category: 'recreation' },
  { match: 'tennis', icon: faTableTennisPaddleBall, category: 'recreation' },
  { match: 'fitness', icon: faDumbbell, category: 'recreation' },
  { match: 'gym', icon: faDumbbell, category: 'recreation' },

  // Activities — water & outdoor / nature (blue)
  { match: 'boating', icon: faSailboat, category: 'climate' },
  { match: 'boat', icon: faSailboat, category: 'climate' },
  { match: 'sailing', icon: faSailboat, category: 'climate' },
  { match: 'kayak', icon: faSailboat, category: 'climate' },
  { match: 'rafting', icon: faSailboat, category: 'climate' },
  { match: 'marina', icon: faAnchor, category: 'climate' },
  { match: 'skiing', icon: faPersonSkiing, category: 'climate' },
  { match: 'ski', icon: faPersonSkiing, category: 'climate' },
  { match: 'fishing', icon: faFish, category: 'climate' },
  { match: 'fish', icon: faFish, category: 'climate' },
  { match: 'snorkel', icon: faWater, category: 'climate' },
  { match: 'diving', icon: faWater, category: 'climate' },
  { match: 'surf', icon: faWater, category: 'climate' },
  { match: 'mountain climbing', icon: faMountain, category: 'climate' },
  { match: 'rock climbing', icon: faMountain, category: 'climate' },
  { match: 'climbing', icon: faMountain, category: 'climate' },

  // Activities — entertainment (amber)
  { match: 'bowling', icon: faBowlingBall, category: 'services' },
  { match: 'casino', icon: faDice, category: 'services' },
  { match: 'gambling', icon: faDice, category: 'services' },
  { match: 'theater', icon: faMasksTheater, category: 'services' },
  { match: 'theatre', icon: faMasksTheater, category: 'services' },

  // Recreation & general (no category -> light blue default)
  { match: 'beach', icon: faUmbrellaBeach, category: 'default' },
  { match: 'swimming pool', icon: faPersonSwimming, category: 'default' },
  { match: 'swimming', icon: faPersonSwimming, category: 'default' },
  { match: 'pool', icon: faPersonSwimming, category: 'default' },
  { match: 'hot tub', icon: faHotTub, category: 'default' },
  { match: 'jacuzzi', icon: faHotTub, category: 'default' },
  { match: 'sightseeing', icon: faBinoculars, category: 'default' },
  { match: 'parking', icon: faCar, category: 'default' },
  { match: 'restaurant', icon: faUtensils, category: 'default' },
  { match: 'dining', icon: faUtensils, category: 'default' },
  { match: 'kitchen', icon: faKitchenSet, category: 'default' },
  { match: 'elevator', icon: faElevator, category: 'default' },
  { match: 'coffee', icon: faMugHot, category: 'default' },
];

export const getCondoAmenityIcon = (name: string): { icon: IconDefinition; category: CondoAmenityCategory } => {
  const normalized = (name || '').toLowerCase();
  const rule = CONDO_AMENITY_RULES.find((r) => normalized.includes(r.match));
  return rule ? { icon: rule.icon, category: rule.category } : { icon: faCircleCheck, category: 'default' };
};

export type CondoAmenityValueColor = 'green' | 'red' | 'grey';

// green for available/yes, red for not available/no, grey for everything else.
export const getCondoAmenityValueColor = (value?: string): CondoAmenityValueColor => {
  const v = (value || '').trim().toLowerCase();
  if (v === 'no' || v.includes('not available')) return 'red';
  if (v === 'yes' || v.includes('available')) return 'green';
  return 'grey';
};

// Proximity pill color: green when on-site, grey otherwise (nearby / offsite / distance).
export const getCondoProximityColor = (proximity?: string): 'green' | 'grey' =>
  (proximity || '').toLowerCase() === 'onsite' ? 'green' : 'grey';
